import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Button } from '@/components/ui/button';
import zamalekImage from '@/assets/zamalek-apartments.jpg';
import sixthOctoberImage from '@/assets/sixth-october-apartments.jpg';
import downtownImage from '@/assets/downtown-apartments.jpg';
import gizaImage from '@/assets/giza-apartments.jpg';
import alexandriaImage from '@/assets/alexandria-apartments.jpg';
interface Property {
  id: string;
  name: string;
  address: string;
  coordinates: [number, number];
  rating: number;
  amenities: string[];
  image: string;
}
interface City {
  name: string;
  coordinates: [number, number];
  properties: number;
}
interface PropertyMapProps {
  selectedProperty?: Property | null;
}
const cities: City[] = [{
  name: 'Cairo',
  coordinates: [31.2357, 30.0444],
  properties: 16
}, {
  name: 'Alexandria',
  coordinates: [29.9187, 31.2001],
  properties: 2
}, {
  name: 'Giza',
  coordinates: [31.2001, 30.0131],
  properties: 2
}, {
  name: 'Hurghada',
  coordinates: [33.8116, 27.2574],
  properties: 1
}, {
  name: 'North Coast',
  coordinates: [29.2000, 30.9000],
  properties: 3
}];
interface LocationButton {
  name: string;
  image: string;
  coordinates: [number, number];
}
const locationButtons: LocationButton[] = [{
  name: 'Zamalek',
  image: zamalekImage,
  coordinates: [31.2278, 30.0618]
}, {
  name: '6th of October',
  image: sixthOctoberImage,
  coordinates: [31.0545, 29.9097]
}, {
  name: 'Downtown',
  image: downtownImage,
  coordinates: [31.2357, 30.0444]
}, {
  name: 'Giza',
  image: gizaImage,
  coordinates: [31.2001, 30.0131]
}, {
  name: 'Alexandria',
  image: alexandriaImage,
  coordinates: [29.9187, 31.2001]
}];
const PropertyMap: React.FC<PropertyMapProps> = ({
  selectedProperty
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [selectedCity, setSelectedCity] = useState<string>('Cairo');
  const [mapboxToken, setMapboxToken] = useState<string>('');
  const [isTokenSet, setIsTokenSet] = useState<boolean>(false);
  const [mapError, setMapError] = useState<string>('');
  const markers = useRef<mapboxgl.Marker[]>([]);
  const [highlightedProperty, setHighlightedProperty] = useState<Property | null>(null);
  const initializeMap = async (token: string) => {
    if (!mapContainer.current || !token) return;
    try {
      setMapError('');
      mapboxgl.accessToken = token;

      // Test the token by making a simple request first
      const testResponse = await fetch(`https://api.mapbox.com/styles/v1/mapbox/outdoors-v12?access_token=${token}`);
      if (!testResponse.ok) {
        throw new Error('Invalid Mapbox token or insufficient permissions');
      }
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/outdoors-v12',
        center: [31.2357, 30.0444],
        zoom: 6,
        pitch: 60,
        bearing: 0,
        antialias: true,
        projection: 'globe'
      });

      // Add navigation controls with pitch and bearing
      map.current.addControl(new mapboxgl.NavigationControl({
        visualizePitch: true,
        showZoom: true,
        showCompass: true
      }), 'top-right');

      // Add 3D terrain and atmosphere effects
      map.current.on('style.load', () => {
        // Add atmosphere for globe projection
        map.current?.setFog({
          'color': '#242f3e',
          // Dark blue-grey like Apple Maps night
          'high-color': '#161b22',
          // Darker at horizon
          'horizon-blend': 0.02,
          'space-color': '#0d1117',
          // Space color
          'star-intensity': 0.6
        });

        // Add 3D terrain
        map.current?.addSource('mapbox-dem', {
          'type': 'raster-dem',
          'url': 'mapbox://mapbox.mapbox-terrain-dem-v1',
          'tileSize': 512,
          'maxzoom': 14
        });
        map.current?.setTerrain({
          'source': 'mapbox-dem',
          'exaggeration': 1.5
        });

        // Add lighting for 3D effect
        map.current?.setLight({
          'color': '#ffffff',
          'intensity': 0.4,
          'position': [1.5, 90, 60]
        });
      });

      // Add markers for all cities
      cities.forEach(city => {
        const markerElement = document.createElement('div');
        markerElement.className = 'w-6 h-6 bg-blue-500 rounded-full border-2 border-white shadow-xl cursor-pointer hover:scale-110 transition-transform relative';
        // Add inner glow effect for Apple-like appearance
        markerElement.style.boxShadow = '0 0 20px rgba(59, 130, 246, 0.6), 0 4px 10px rgba(0, 0, 0, 0.3)';
        const marker = new mapboxgl.Marker(markerElement).setLngLat(city.coordinates).setPopup(new mapboxgl.Popup({
          offset: 25,
          className: 'dark-popup'
        }).setHTML(`
              <div class="p-3 bg-gray-800 text-white rounded-lg">
                <h3 class="font-semibold text-white">${city.name}</h3>
                <p class="text-sm text-gray-300">${city.properties} serviced apartments</p>
              </div>
            `)).addTo(map.current!);
        markers.current.push(marker);
      });
    } catch (error) {
      console.error('Map initialization error:', error);
      setMapError(error instanceof Error ? error.message : 'Failed to initialize map');
      setIsTokenSet(false);
    }
  };
  const handleLocationSelect = (location: LocationButton) => {
    if (!map.current) return;
    map.current.flyTo({
      center: location.coordinates,
      zoom: 14,
      pitch: 45,
      bearing: 0,
      duration: 2000,
      essential: true
    });
  };
  const handleCitySelect = (city: City) => {
    if (!map.current) return;
    setSelectedCity(city.name);
    map.current.flyTo({
      center: city.coordinates,
      zoom: 11,
      pitch: 50,
      bearing: 0,
      duration: 2000,
      essential: true
    });
  };
  const handleTokenSubmit = () => {
    if (mapboxToken.trim()) {
      setIsTokenSet(true);
      initializeMap(mapboxToken.trim());
    }
  };

  // Effect to handle selected property changes
  useEffect(() => {
    if (selectedProperty && map.current) {
      setHighlightedProperty(selectedProperty);

      // Fly to the selected property
      map.current.flyTo({
        center: selectedProperty.coordinates,
        zoom: 16,
        pitch: 60,
        bearing: 0,
        duration: 2000,
        essential: true
      });

      // Add a special marker for the selected property
      const selectedMarkerElement = document.createElement('div');
      selectedMarkerElement.className = 'w-8 h-8 bg-red-500 rounded-full border-4 border-white shadow-xl cursor-pointer animate-pulse relative';
      selectedMarkerElement.style.boxShadow = '0 0 30px rgba(239, 68, 68, 0.8), 0 4px 15px rgba(0, 0, 0, 0.4)';
      const selectedMarker = new mapboxgl.Marker(selectedMarkerElement).setLngLat(selectedProperty.coordinates).setPopup(new mapboxgl.Popup({
        offset: 25,
        className: 'property-popup'
      }).setHTML(`
          <div class="p-4 bg-white rounded-lg shadow-lg">
            <h3 class="font-semibold text-gray-900 mb-2">${selectedProperty.name}</h3>
            <p class="text-sm text-gray-600 mb-2">${selectedProperty.address}</p>
            <div class="flex items-center space-x-2 mb-2">
              <span class="text-yellow-500">★</span>
              <span class="text-sm font-medium">${selectedProperty.rating}</span>
            </div>
            <div class="flex flex-wrap gap-1">
              ${selectedProperty.amenities.slice(0, 3).map(amenity => `<span class="text-xs bg-gray-100 px-2 py-1 rounded">${amenity}</span>`).join('')}
            </div>
          </div>
        `)).addTo(map.current!);

      // Remove previous selected marker if exists
      markers.current.forEach(marker => {
        if (marker.getElement().classList.contains('bg-red-500')) {
          marker.remove();
        }
      });
      markers.current.push(selectedMarker);
    }
  }, [selectedProperty]);
  useEffect(() => {
    if (mapboxToken && !map.current && !mapError) {
      initializeMap(mapboxToken);
    }
    return () => {
      // Safely cleanup markers
      try {
        markers.current.forEach(marker => {
          if (marker && typeof marker.remove === 'function') {
            marker.remove();
          }
        });
        markers.current = [];
      } catch (error) {
        console.warn('Error cleaning up markers:', error);
      }

      // Safely cleanup map
      try {
        if (map.current && typeof map.current.remove === 'function') {
          map.current.remove();
          map.current = null;
        }
      } catch (error) {
        console.warn('Error cleaning up map:', error);
      }
    };
  }, []);
  return <div className="w-full">
      {!isTokenSet}

      {isTokenSet && !mapError && <div className="relative">
          <div ref={mapContainer} className="w-full h-[600px] rounded-xl shadow-xl border border-gray-200" />
          <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-transparent via-transparent to-background/10 rounded-xl" />
        </div>}

      {mapError && isTokenSet && <div className="bg-red-50 p-6 rounded-lg mb-8 max-w-md mx-auto">
          <h3 className="text-lg font-medium text-red-900 mb-2">Map Error</h3>
          <p className="text-sm text-red-700 mb-4">{mapError}</p>
          <Button onClick={() => {
        setIsTokenSet(false);
        setMapError('');
      }} variant="outline">
            Try Different Token
          </Button>
        </div>}
    </div>;
};
export default PropertyMap;